import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push, replace } from 'react-router-redux';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Dimmer,
  Icon,
  Label,
  Loader,
  Message,
  Progress,
  Table,
} from 'semantic-ui-react';
import qs from 'qs';

import NavBar from '../../components/NavBar';
import PageNav from '../../components/PageNav';
import Timestamp from '../../components/Timestamp';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { decodeSearch, encodeSearch } from '../../utils/url';
import fileSize from '../../utils/fileSize';
import valueToText from '../../utils/valueToText';
import { isVietnamCompany } from '../../utils/vietnam';
import {
  exportFiltersFromQuery,
  exportFiltersSummary,
  progressPercent,
} from '../../utils/vnBookingExport';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

// CPI-1281: Booking Exports. The Bookings list is served by the CRM in pages behind a short-lived
// cursor, so a download cannot be streamed from one request. This page queues an export carrying
// the filters the user arrived with; book-api's cron builds the workbook and the row flips to
// completed with a download link. Vietnam only.
const POLL_MS = 3000;
const STATUSES = [
  { text: 'Generating', value: 'new' },
  { text: 'Completed', value: 'completed' },
  { text: 'Failed', value: 'failed' },
];

class Exports extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    auth: PropTypes.shape({
      company: PropTypes.shape({ countryCode: PropTypes.string }),
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }).isRequired,
    t: PropTypes.func,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
  })
  constructor(props) {
    super(props);
    const query = decodeSearch(props.location.search);
    this.state = {
      loading: true,
      generating: false,
      token: '',
      data: [],
      count: 0,
      limit: 0,
      offset: 0,
      currentPage: parseInt(query.page, 10) || 1,
      filters: exportFiltersFromQuery(query),
    };
  }
  componentDidMount() {
    this.mounted = true;
    const { store } = this.context;
    if (!isVietnamCompany(this.props.auth)) {
      store.dispatch(replace('/opportunities/'));
      return;
    }
    this.fetchExports();
    this.ticker = setInterval(() => {
      if (this.state.generating && this.state.currentPage === 1) {
        this.fetchExports(true);
      }
    }, POLL_MS);
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      const query = decodeSearch(nextProps.location.search);
      this.setState({
        currentPage: parseInt(query.page, 10) || 1,
        filters: exportFiltersFromQuery(query),
      }, () => this.fetchExports());
    }
  }
  componentWillUnmount() {
    this.mounted = false;
    if (this.ticker) clearInterval(this.ticker);
  }
  setStateSafe(state) {
    return new Promise((res) => {
      if (!this.mounted) { res(); return; }
      this.setState(state, res);
    });
  }
  async fetchExports(silent = false) {
    const { store } = this.context;
    const { t } = this.props;
    if (!silent) await this.setStateSafe({ loading: true });
    let res;
    try {
      res = await store.dispatch(fetchM(`/opportunity/export/?${qs.stringify({ page: this.state.currentPage })}`, {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }));
    } catch (err) {
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
      await this.setStateSafe({ loading: false });
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      store.dispatch(flashErrorMessage({ message }));
      await this.setStateSafe({ loading: false });
      return;
    }
    const data = await res.json();
    await this.setStateSafe({
      loading: false,
      token: res.headers.get('X-Csrf-Token') || this.state.token,
      count: parseInt(res.headers.get('M-Data-count'), 10) || 0,
      limit: parseInt(res.headers.get('M-Data-limit'), 10) || 0,
      offset: parseInt(res.headers.get('M-Data-offset'), 10) || 0,
      data: data || [],
      generating: (data || []).some(row => row.statusID === 'new'),
    });
  }
  handleGenerate = async (e) => {
    e.preventDefault();
    const { store } = this.context;
    const { t } = this.props;
    await this.setStateSafe({ loading: true });
    try {
      await store.dispatch(fetchM(`/opportunity/export/?${qs.stringify(this.state.filters)}`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
      }));
    } catch (err) {
      await this.setStateSafe({ loading: false });
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
      return;
    }
    store.dispatch(flashSuccessMessage({
      header: t('Generating Export'),
      message: `${t('Export has been added to the queue')}.`,
    }));
    if (this.state.currentPage !== 1) {
      this.handleChangePage(1);
      return;
    }
    this.fetchExports();
  }
  handleChangePage = (currentPage) => {
    const { store } = this.context;
    const query = decodeSearch(this.props.location.search);
    query.page = currentPage;
    store.dispatch(replace(`${this.props.location.pathname}?${encodeSearch(query)}`));
  }
  handleBack = (e) => {
    e.preventDefault();
    const { store } = this.context;
    const search = qs.stringify(this.state.filters);
    store.dispatch(push(search ? `/opportunities/?${search}` : '/opportunities/'));
  }
  renderFilters(filters) {
    const { t } = this.props;
    const parts = exportFiltersSummary(filters, t, { ns: TRANSLATION_FILES.constants });
    return parts.map(part => (
      <Label key={part.key} basic size="small" style={{ marginBottom: '0.25em' }}>
        {part.label ? part.label : part.value}
        {part.label ? <Label.Detail>{part.value}</Label.Detail> : null}
      </Label>
    ));
  }
  renderRow = (file) => {
    const { t } = this.props;
    const completed = file.statusID === 'completed';
    return (
      <Table.Row key={file.id}>
        <Table.Cell width={5}>
          {completed ? (
            <a
              href={`${config.get('bookApiURL')}/opportunity/export/${file.id}/${file.name}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <b><Icon name="download" /> {file.name}</b>
            </a>
          ) : (
            <span><Icon name="file excel outline" /> {file.name}</span>
          )}
          {file.statusID === 'failed' && file.errorMessage ? (
            <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>{file.errorMessage}</div>
          ) : null}
        </Table.Cell>
        <Table.Cell>{this.renderFilters(file.filters)}</Table.Cell>
        <Table.Cell collapsing>{file.requestedByName || '-'}</Table.Cell>
        <Table.Cell collapsing textAlign="right">{completed ? file.rowCount : '-'}</Table.Cell>
        <Table.Cell collapsing>{file.size ? fileSize(file.size) : '-'}</Table.Cell>
        <Table.Cell collapsing><Timestamp format="DD/M/YYYY HH:mm" timestamp={file.createdAt} /></Table.Cell>
        <Table.Cell width={3}>
          <Progress
            size="small"
            percent={completed ? 100 : progressPercent(file.progress)}
            progress
            color={file.statusID === 'new' ? 'blue' : null}
            success={completed}
            error={file.statusID === 'failed'}
            active={file.statusID === 'new'}
          >
            {t(valueToText(STATUSES, file.statusID), { ns: TRANSLATION_FILES.constants })}
          </Progress>
        </Table.Cell>
      </Table.Row>
    );
  }
  render() {
    const { t } = this.props;
    const { loading, generating, data, count, limit, offset, currentPage, filters } = this.state;
    return (
      <Container>
        <NavBar
          header={t('Booking Exports')}
          leftNav={(
            <Button basic icon="arrow left" content={t('Bookings')} onClick={this.handleBack} />
          )}
        />
        <Dimmer inverted active={loading}>
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <Message info size="small">
          <Message.Content>
            <p style={{ marginBottom: '0.5em' }}>{t('The export runs in the background and appears below once it is ready')}.</p>
            <div>
              <b>{t('Filters')}:</b> {this.renderFilters(filters)}
            </div>
          </Message.Content>
        </Message>
        <Table stackable selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell colSpan={4}>
                <h3>{t('Booking Exports')}</h3>
              </Table.HeaderCell>
              <Table.HeaderCell colSpan={3} textAlign="right">
                <Button primary onClick={this.handleGenerate} disabled={generating || loading} data-bot-target="generate-export">
                  {generating ? <Icon loading name="spinner" /> : <Icon name="download" />}
                  {generating ? t('Generating') : t('Generate Export')}
                </Button>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Header className="mobile-hide">
            <Table.Row>
              <Table.HeaderCell>{t('Name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Filters')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Requested by')}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{t('Rows')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Size')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Date')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Status')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.length > 0 ? data.map(this.renderRow) : (
              <Table.Row>
                <Table.Cell colSpan={7} textAlign="center">{t('No exports found')}.</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={7} textAlign="center">
                <PageNav
                  upward
                  currentPage={currentPage}
                  count={count}
                  limit={limit}
                  offset={offset}
                  dataSize={data.length}
                  loading={loading}
                  onChange={this.handleChangePage}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(connect(Exports.mapStateToProps)(Exports));



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Exports.jsx